export type LandingRecordType =
  | 'Submission__c'
  | 'Prospect__c'
  | 'Applicant__c';

export interface LandingRecord {
  id: string;
  type: LandingRecordType;
}

const LANDING_PATHS: Array<[string, LandingRecordType]> = [
  ['/agent/:id', 'Submission__c'],
  ['/agent/loan/:id', 'Submission__c'],
  ['/booking/:id', 'Submission__c'],
  ['/booking/portal/:id', 'Submission__c'],
  ['/get-offers/:id', 'Submission__c'],
  ['/my-offers/:id', 'Submission__c'],
  ['/my-offers/5kf/:id', 'Submission__c'],
  ['/my-offers/pennie/:id', 'Submission__c'],
  ['/my-offers/referral/:id', 'Submission__c'],
  ['/my-offers/together/:id', 'Submission__c'],
  ['/my-offers/tri-point/:id', 'Submission__c'],
  ['/offerhub/:id', 'Prospect__c'],
  ['/portal/:id', 'Prospect__c'],
  ['/portal/tpl/:id', 'Prospect__c'],
  ['/portal/gf/:id', 'Applicant__c'],
  ['/portal/ls/:id', 'Applicant__c'],
];

const LANDING_QUERY: Array<[string, LandingRecordType]> = [
  ['submission_id', 'Submission__c'],
  ['prospectId', 'Prospect__c'],
];

export const LINK_TOKEN_PARAM = 't';

export const LANDING_QUERY_PARAMS = [
  ...LANDING_QUERY.map(([name]) => name),
  LINK_TOKEN_PARAM,
];

const ID_SHAPE = '([a-zA-Z0-9]{15}(?:[a-zA-Z0-9]{3})?)';
const QUERY_ID = new RegExp(`^${ID_SHAPE}$`);

const LANDING_PATTERNS = LANDING_PATHS.map(
  ([path, type]) =>
    [new RegExp(`^${path.replace(':id', ID_SHAPE)}/?$`), type] as const
);

export function landingRecords(url: URL): LandingRecord[] {
  const fromPath = LANDING_PATTERNS.map(([pattern, type]) => {
    const id = url.pathname.match(pattern)?.[1];
    return id ? { id, type } : null;
  });
  const fromQuery = LANDING_QUERY.map(([name, type]) => {
    const id = url.searchParams.get(name);
    return id && QUERY_ID.test(id) ? { id, type } : null;
  });
  return [...fromPath, ...fromQuery].filter(
    (record): record is LandingRecord => record !== null
  );
}

export function landingSubmissionId(url: URL): string | null {
  return (
    landingRecords(url).find(({ type }) => type === 'Submission__c')?.id ?? null
  );
}

export function withLinkToken(path: string, token: string | null | undefined) {
  if (!token) return path;
  const separator = path.includes('?') ? '&' : '?';
  return `${path}${separator}${LINK_TOKEN_PARAM}=${encodeURIComponent(token)}`;
}

// Sets ?t= on a page address to a token the server just issued, replacing any
// t already there, so a stale token on an old link cannot shadow it.
export function withEarnedLinkToken(path: string, token: string) {
  const url = new URL(path, 'https://page.invalid');
  url.searchParams.set(LINK_TOKEN_PARAM, token);
  return `${url.pathname}${url.search}`;
}
